import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAppData } from '../MainApp.js';
import { SEC_ApiCall } from '../func.js';
import LoadingComp from '../common/loading.js';
import Header from '../common/header.js';
import './Alert.css';

/**
 * Landing view for the Alert module. Provides a friendly placeholder with
 * module context until the full workflow is implemented.
 */
export default function AlertHome() {
    const displayName = userAppData?.displayName || 'Team Member';
    const schoolName = userAppData?.schoolName || 'your school';
    const canManageAlerts = userAppData?.alertData?.canManageAlerts === true;
    const navigate = useNavigate();
    const [bootstrapData, setBootstrapData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState(false);

    const fetchAlertHome = useCallback(async () => {
        setApiError(false);
        try {
            const response = await SEC_ApiCall('alert/home', {}, setApiError);
            setBootstrapData(response);
        } catch (error) {
            console.error('Failed to load alert home data', error);
            setApiError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAlertHome();
    }, [fetchAlertHome]);

    const activeAlerts = useMemo(() => {
        if (!bootstrapData?.activeAlerts || !Array.isArray(bootstrapData.activeAlerts)) {
            return [];
        }
        return bootstrapData.activeAlerts;
    }, [bootstrapData]);

    const previousAlerts = useMemo(() => {
        if (!bootstrapData?.previousAlerts || !Array.isArray(bootstrapData.previousAlerts)) {
            return [];
        }
        return bootstrapData.previousAlerts;
    }, [bootstrapData]);

    const handleCreateClick = () => {
        navigate('/app/alert/new', { state: { alertBootstrap: bootstrapData } });
    };

    /**
     * Normalises the various alert identifier shapes the backend may return so clicks
     * always navigate correctly, even for legacy records that only store the GUID in SK.
     */
    const resolveAlertId = (alert) => {
        if (!alert) {
            return null;
        }
        const candidates = [
            alert.alertId,
            alert.id,
            alert.alertID,
            alert.alert_id,
            alert.SK,
            alert.alertGuid,
            alert.alertGUID,
            alert.guid,
        ];
        const candidate = candidates.find(Boolean);
        if (candidate) {
            if (typeof candidate === 'object') {
                if (typeof candidate.S === 'string') {
                    return candidate.S;
                }
                if (typeof candidate.value === 'string') {
                    return candidate.value;
                }
                if (typeof candidate.id === 'string') {
                    return candidate.id;
                }
            }
            return String(candidate);
        }

        // Legacy alerts may only provide an alertUrl that contains the GUID.
        if (alert.alertUrl && typeof alert.alertUrl === 'string') {
            try {
                const parsedUrl = new URL(alert.alertUrl);
                const guidParam =
                    parsedUrl.searchParams.get('alertGuid') ||
                    parsedUrl.searchParams.get('alertGUID') ||
                    parsedUrl.searchParams.get('guid');
                if (guidParam) {
                    return guidParam;
                }
            } catch (err) {
                console.warn('Unable to parse alertUrl for alertId', err);
            }
        }

        return null;
    };

    const handleAlertClick = (alert) => {
        const alertId = resolveAlertId(alert);
        if (!alertId) {
            console.warn('Alert click ignored because no identifier was found', alert);
            return;
        }
        navigate(`/app/alert/single/${alertId}`);
    };

    // Show a centred loader to avoid flashing the main UI before data arrives
    if (loading) {
        return (
            <div className='alert-home-container alert-home-loading'>
                <LoadingComp />
            </div>
        );
    }

    return (
        <div className='alert-home-container'>
            <Header
                title='Alert Centre'
                rightContent={canManageAlerts ? (
                <button
                    type="button"
                    className="module-page-header__button"
                    onClick={handleCreateClick}
                    disabled={loading}
                >
                    {loading ? 'Loading...' : 'Create New Alert'}
                </button>
                ) : null}
            />

            {apiError && (
                <p className="alert-home-body alert-home-error" role="alert">
                    We could not load alert data right now. Please try again shortly.
                </p>
            )}

            <div className='alert-home-card alert-home-active-card'>
                <div className='alert-home-header'>
                    <div>
                        <h2 className='alert-home-heading'>Active Alerts</h2>
                        <p className='alert-home-body alert-home-subhead'>
                            Live alerts appear here while they are active.
                        </p>
                    </div>
                </div>

                {!loading && activeAlerts.length === 0 && (
                    <div className="alert-active-empty">
                        <p className="alert-active-empty-text">No active alerts</p>
                    </div>
                )}

                {!loading && activeAlerts.length > 0 && (
                    <div className="alert-active-banner" role="alert">
                        {activeAlerts.map((activeAlert) => {
                            const bgColour = activeAlert.bgColour || '#b02020';
                            const textColour = activeAlert.textColour || '#ffffff';
                            return (
                                <div
                                    className="alert-active-card alert-card--link"
                                    key={activeAlert.alertId || activeAlert.title}
                                    style={{ backgroundColor: bgColour, color: textColour, borderColor: textColour }}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleAlertClick(activeAlert)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            handleAlertClick(activeAlert);
                                        }
                                    }}
                                >
                                    <div className="alert-active-text">
                                        <p className="alert-active-kicker" style={{ color: textColour }}>Active Alert</p>
                                        <h2 className="alert-active-title" style={{ color: textColour }}>{activeAlert.title || 'Active alert in progress'}</h2>
                                        {activeAlert.subtitle && (
                                            <p className="alert-active-body" style={{ color: textColour }}>{activeAlert.subtitle}</p>
                                        )}
                                    </div>
                                    <div className="alert-active-meta">
                                        {activeAlert.type && (
                                            <span className="alert-chip">{activeAlert.type}</span>
                                        )}
                                        {activeAlert.severity && (
                                            <span className="alert-chip alert-chip--tone">{activeAlert.severity}</span>
                                        )}
                                        {activeAlert.validUntil && (
                                            <span className="alert-chip alert-chip--muted">
                                                Until: {activeAlert.validUntil}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className='alert-home-card alert-home-active-card'>
                <div className='alert-home-header'>
                    <div>
                        <h2 className='alert-home-heading'>Previous Alerts</h2>
                        <p className='alert-home-body alert-home-subhead'>
                            The last five ended alerts for quick reference.
                        </p>
                    </div>
                </div>

                {previousAlerts.length === 0 && (
                    <div className="alert-active-empty">
                        <p className="alert-active-empty-text">No previous alerts</p>
                    </div>
                )}

                {previousAlerts.length > 0 && (
                    <div className="alert-previous-banner" role="region" aria-label="Previous alerts">
                        <div className="alert-previous-grid">
                            {previousAlerts.map((alert) => {
                                const bgColour = alert.bgColour || '#fff9f0';
                                const textColour = alert.textColour || '#706f6f';
                                return (
                                    <div
                                        className="alert-previous-card alert-card--link"
                                        key={alert.alertId || alert.title}
                                        style={{ backgroundColor: bgColour, color: textColour, borderColor: textColour }}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleAlertClick(alert)}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                handleAlertClick(alert);
                                            }
                                        }}
                                    >
                                        <div className="alert-active-text">
                                            <h2 className="alert-active-title" style={{ color: textColour }}>{alert.title || 'Alert'}</h2>
                                            {alert.subtitle && (
                                                <p className="alert-active-body" style={{ color: textColour }}>{alert.subtitle}</p>
                                            )}
                                        </div>
                                        <div className="alert-active-meta">
                                            {alert.type && (
                                                <span className="alert-chip">{alert.type}</span>
                                            )}
                                            {alert.severity && (
                                                <span className="alert-chip alert-chip--tone">{alert.severity}</span>
                                            )}
                                            {alert.validUntil && (
                                                <span className="alert-chip alert-chip--muted">
                                                    Until: {alert.validUntil}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
